import { Link } from "react-router-dom";
import { useEffect, useRef } from "react";
import {
    ArrowUpRight,
    PhoneCall,
    Truck,
    PackageCheck,
    ShieldCheck,
    Headphones,
    Network,
    Zap,
    Quote,
} from "lucide-react";
import Hero3D from "@/components/Hero3D";

const STEPS = [
    {
        n: "01",
        icon: PhoneCall,
        title: "You contact us",
        desc: "Send your load details — origin, destination, cargo, timing. One message. One call. That's all we need.",
    },
    {
        n: "02",
        icon: Network,
        title: "We find the right carrier",
        desc: "Our team taps a vetted network and matches you with the best carrier on price, capacity, and reliability.",
    },
    {
        n: "03",
        icon: PackageCheck,
        title: "We manage the delivery",
        desc: "From dispatch to drop-off, we coordinate every leg. You get one point of contact and zero admin.",
    },
];

const REASONS = [
    {
        icon: Headphones,
        title: "Single point of contact",
        desc: "One team. One inbox. Every load handled by people who know your operation.",
    },
    {
        icon: Zap,
        title: "Faster coordination",
        desc: "We respond in minutes, not hours. Internal tooling lets us match and dispatch faster.",
    },
    {
        icon: ShieldCheck,
        title: "Trusted carrier network",
        desc: "Every carrier vetted for compliance, equipment, and reliability — across borders.",
    },
    {
        icon: Truck,
        title: "Less admin for everyone",
        desc: "We handle paperwork, tracking, and updates. You handle your business.",
    },
];

const STATS = [
    { v: "10k+", l: "Loads coordinated" },
    { v: "<10m", l: "Average response" },
    { v: "98%", l: "On-time delivery" },
    { v: "24/7", l: "Live coordination" },
];

const ROUTES = [
    "JNB → CPT",
    "DBN → JNB",
    "CPT → WIN",
    "JNB → GBE",
    "JNB → MPM",
    "DUR → HRE",
    "CPT → LUS",
    "JNB → MAS",
];

const Home = () => {
    const heroRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) e.target.classList.add("animate-fade-up");
                });
            },
            { threshold: 0.1 }
        );
        document.querySelectorAll("[data-reveal]").forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section
                ref={heroRef}
                className="relative min-h-screen overflow-hidden"
            >
                <div className="absolute inset-0 grid-bg opacity-40" />
                <div className="absolute inset-0">
                    <Hero3D />
                </div>
                <div
                    className="absolute inset-y-0 left-0 w-full md:w-3/5 pointer-events-none z-[2]"
                    style={{
                        background:
                            "linear-gradient(90deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.85) 35%, rgba(255,255,255,0.4) 65%, rgba(255,255,255,0) 100%)",
                    }}
                />

                <div className="relative z-10 mx-auto max-w-7xl px-5 md:px-8 pt-40 md:pt-44 pb-32 pointer-events-none">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-2 mono text-xs text-brand-green border border-brand-green/30 rounded-full px-3 py-1.5 mb-8 bg-brand-green/5 pointer-events-auto">
                            <span className="h-1.5 w-1.5 rounded-full bg-brand-green animate-pulse" />
                            FULLY MANAGED LOGISTICS BROKERAGE
                        </div>

                        <h1
                            data-testid="hero-headline"
                            className="display text-5xl sm:text-6xl lg:text-7xl text-[#1a1a1a]"
                        >
                            We Move It<br />
                            <span className="glow-text text-brand-green">
                                For You.
                            </span>
                        </h1>

                        <p className="mt-8 text-base md:text-lg text-gray-700 max-w-xl leading-relaxed">
                            Nexship connects your loads with the right carriers
                            — fully managed by our team. No platforms to learn,
                            no carriers to chase. Just one trusted partner
                            handling the whole route.
                        </p>

                        <div className="mt-10 flex flex-wrap gap-4 pointer-events-auto">
                            <Link
                                to="/request-transport"
                                data-testid="hero-cta-primary"
                                className="btn-primary"
                            >
                                Request Transport <ArrowUpRight size={18} />
                            </Link>
                            <Link
                                to="/services"
                                data-testid="hero-cta-secondary"
                                className="btn-ghost"
                            >
                                See Our Services
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Bottom marquee */}
                <div className="absolute bottom-0 left-0 right-0 z-10 border-y border-gray-200 bg-white/80 backdrop-blur-md">
                    <div className="scroll-marquee py-3">
                        <div className="flex gap-12 animate-marquee whitespace-nowrap mono text-xs text-gray-500">
                            {[...ROUTES, ...ROUTES, ...ROUTES].map((r, i) => (
                                <span key={i} className="flex items-center gap-3">
                                    <span className="h-1 w-1 rounded-full bg-brand-green" />
                                    {r}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* STATS */}
            <section className="relative py-20 border-b border-gray-200">
                <div className="mx-auto max-w-7xl px-5 md:px-8">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-gray-50 rounded-2xl overflow-hidden">
                        {STATS.map((s, i) => (
                            <div
                                key={i}
                                className="bg-white p-8 text-center"
                                data-testid={`stat-${i}`}
                            >
                                <p className="display text-4xl md:text-5xl text-brand-green">
                                    {s.v}
                                </p>
                                <p className="mono text-xs text-gray-500 mt-2 uppercase">
                                    {s.l}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section
                className="relative py-28 md:py-36"
                data-testid="how-it-works"
            >
                <div className="mx-auto max-w-7xl px-5 md:px-8">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
                        <div>
                            <p className="mono text-xs text-brand-green uppercase mb-4">
                                / How it works
                            </p>
                            <h2 className="display text-4xl md:text-5xl lg:text-6xl text-[#1a1a1a] max-w-2xl">
                                Three steps. <br />
                                <span className="text-gray-500">
                                    Zero friction.
                                </span>
                            </h2>
                        </div>
                        <p className="text-gray-600 text-base max-w-md">
                            We've stripped logistics down to what matters. You
                            stay in your business. We handle the moving parts.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-5">
                        {STEPS.map((s, i) => (
                            <div
                                key={s.n}
                                data-reveal
                                data-testid={`step-${i}`}
                                className="relative group brand-border-soft rounded-2xl p-8 hover:translate-y-[-4px] transition-all duration-300"
                                style={{ animationDelay: `${i * 120}ms` }}
                            >
                                <div className="flex items-start justify-between mb-8">
                                    <span className="mono text-xs text-brand-green">
                                        {s.n}
                                    </span>
                                    <div className="h-12 w-12 rounded-xl bg-brand-green/10 flex items-center justify-center group-hover:bg-brand-green/20 transition-colors">
                                        <s.icon
                                            size={20}
                                            className="text-brand-green"
                                        />
                                    </div>
                                </div>
                                <h3 className="text-xl font-semibold text-[#1a1a1a] mb-3">
                                    {s.title}
                                </h3>
                                <p className="text-sm text-gray-600 leading-relaxed">
                                    {s.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHY NEXSHIP */}
            <section
                className="relative py-28 md:py-36 border-t border-gray-200"
                data-testid="why-nexship"
            >
                <div className="absolute inset-0 grid-bg opacity-20 pointer-events-none" />
                <div className="relative mx-auto max-w-7xl px-5 md:px-8">
                    <div className="grid md:grid-cols-12 gap-10">
                        <div className="md:col-span-5">
                            <p className="mono text-xs text-brand-green uppercase mb-4">
                                / Why Nexship
                            </p>
                            <h2 className="display text-4xl md:text-5xl text-[#1a1a1a]">
                                Service-driven. <br />
                                Tech-powered.
                            </h2>
                            <p className="mt-6 text-gray-600 leading-relaxed">
                                We are not a marketplace. We are not an app. We
                                are a logistics partner that uses smart internal
                                systems to move your goods reliably — without
                                making you a logistics expert.
                            </p>
                            <Link
                                to="/about"
                                className="mt-8 inline-flex items-center gap-2 text-brand-green hover:text-brand-blue mono text-sm"
                            >
                                MORE ABOUT US <ArrowUpRight size={16} />
                            </Link>
                        </div>

                        <div className="md:col-span-7 grid sm:grid-cols-2 gap-4">
                            {REASONS.map((r, i) => (
                                <div
                                    key={r.title}
                                    data-reveal
                                    data-testid={`reason-${i}`}
                                    className="glass rounded-2xl p-7 hover:border-brand-green/30 hover:bg-gray-50 transition-all"
                                >
                                    <div className="h-11 w-11 rounded-lg bg-brand-blue/10 flex items-center justify-center mb-5">
                                        <r.icon
                                            size={20}
                                            className="text-brand-blue"
                                        />
                                    </div>
                                    <h3 className="text-base font-semibold text-[#1a1a1a] mb-2">
                                        {r.title}
                                    </h3>
                                    <p className="text-sm text-gray-600 leading-relaxed">
                                        {r.desc}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* FOR CUSTOMERS / FOR CARRIERS */}
            <section
                className="relative py-28 md:py-36 border-t border-gray-200"
                data-testid="for-section"
            >
                <div className="mx-auto max-w-7xl px-5 md:px-8 grid md:grid-cols-2 gap-6">
                    <div className="relative overflow-hidden rounded-3xl p-10 md:p-14 brand-border-soft group">
                        <div className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-brand-green/15 blur-3xl group-hover:scale-110 transition-transform duration-700" />
                        <p className="mono text-xs text-brand-green uppercase mb-3">
                            / For Customers
                        </p>
                        <h3 className="display text-3xl md:text-4xl text-[#1a1a1a] mb-4">
                            Move freight without<br />the headache.
                        </h3>
                        <ul className="mt-6 space-y-3 text-gray-700 text-sm">
                            {[
                                "No carriers to chase or vet",
                                "Faster response and coordination",
                                "One point of contact for everything",
                                "Transparent pricing on every load",
                            ].map((t, i) => (
                                <li key={i} className="flex items-start gap-3">
                                    <span className="mt-1 h-1.5 w-1.5 rounded-full bg-brand-green flex-shrink-0" />
                                    {t}
                                </li>
                            ))}
                        </ul>
                        <Link
                            to="/request-transport"
                            data-testid="for-customers-cta"
                            className="btn-primary mt-10 text-sm"
                        >
                            Request Transport <ArrowUpRight size={16} />
                        </Link>
                    </div>

                    <div className="relative overflow-hidden rounded-3xl p-10 md:p-14 brand-border-soft group">
                        <div className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-brand-blue/15 blur-3xl group-hover:scale-110 transition-transform duration-700" />
                        <p className="mono text-xs text-brand-blue uppercase mb-3">
                            / For Carriers
                        </p>
                        <h3 className="display text-3xl md:text-4xl text-[#1a1a1a] mb-4">
                            Consistent loads.<br />Less admin.
                        </h3>
                        <ul className="mt-6 space-y-3 text-gray-700 text-sm">
                            {[
                                "Steady access to vetted work",
                                "No customer hunting required",
                                "Clear instructions, every load",
                                "Quick payment cycles",
                            ].map((t, i) => (
                                <li key={i} className="flex items-start gap-3">
                                    <span className="mt-1 h-1.5 w-1.5 rounded-full bg-brand-blue flex-shrink-0" />
                                    {t}
                                </li>
                            ))}
                        </ul>
                        <Link
                            to="/carrier-signup"
                            data-testid="for-carriers-cta"
                            className="btn-ghost mt-10 text-sm"
                        >
                            Become a Carrier <ArrowUpRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* TESTIMONIAL */}
            <section className="relative py-28 border-t border-gray-200">
                <div className="mx-auto max-w-4xl px-5 md:px-8 text-center">
                    <Quote className="text-brand-green mx-auto mb-6" size={40} />
                    <p className="display text-2xl md:text-4xl text-[#1a1a1a] leading-snug">
                        "Nexship feels like having an in-house logistics team.
                        We send the load, they handle the rest."
                    </p>
                    <p className="mt-8 mono text-xs text-gray-500 uppercase">
                        Logistics manager — manufacturing client
                    </p>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="relative py-28 md:py-36">
                <div className="mx-auto max-w-5xl px-5 md:px-8 text-center">
                    <h2 className="display text-4xl md:text-6xl text-[#1a1a1a]">
                        Let us handle<br />
                        <span className="text-brand-green glow-text">
                            your logistics.
                        </span>
                    </h2>
                    <p className="mt-6 text-gray-600 max-w-xl mx-auto">
                        Tell us what needs to move. We'll take it from there.
                    </p>
                    <div className="mt-10 flex flex-wrap gap-4 justify-center">
                        <Link
                            to="/request-transport"
                            data-testid="final-cta-request"
                            className="btn-primary"
                        >
                            Request Transport <ArrowUpRight size={18} />
                        </Link>
                        <Link
                            to="/contact"
                            data-testid="final-cta-contact"
                            className="btn-ghost"
                        >
                            Talk To Us
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Home;
